/**
 * Enhanced AI & AWS Monitor Component - Production Version
 * Real-time monitoring with comprehensive cost tracking and live data integration
 * July 19, 2025 - Integrated AI & AWS monitoring
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  Alert,
  Paper,
  useTheme,
  alpha,
  Skeleton,
  Avatar,
  AlertTitle,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Queue as QueueIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SmartToy as AIIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  TextFields as TextFieldsIcon,
  Image as ImageIcon,
  Transform as TransformIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Savings as SavingsIcon,
  CloudQueue as CloudIcon,
  Storage as StorageIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { resolveRangeYmd, toApiToken } from '../common/timeRange';
import { format, subDays, startOfDay, endOfDay, startOfMonth, startOfYear, endOfYear, subYears } from 'date-fns';
import '../dashboard/MetricCard.css';

interface QueueTask {
  id: string;
  type: 'chapter_generation' | 'style_application' | 'mood_enhancement' | 'content_transformation' | 'image_generation' | 'legacy_quest';
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  userId: number;
  userEmail: string;
  productType: 'unlimited_subscription' | 'chapter_credits' | 'individual_purchase';
  grokTokensUsed: number;
  grokCost: number;
  estimatedCost: number;
  actualCost: number | null;
  progress: number;
  createdAt: string;
  completedAt: string | null;
  costBreakdown: {
    textGeneration: number;
    imageGeneration: number;
    styleApplication: number;
    moodEnhancement: number;
  };
}

interface QueueStats {
  totalTasks: number;
  pendingTasks: number;
  processingTasks: number;
  completedTasks: number;
  failedTasks: number;
  successRate: number;
  totalCostToday: number;
  totalCostThisMonth: number;
  averageCostPerTask: number;
  grokTokensUsedToday: number;
  costByProduct: {
    unlimited_subscription: number;
    chapter_credits: number;
    individual_purchase: number;
  };
  costByTaskType: {
    chapter_generation: number;
    style_application: number;
    mood_enhancement: number;
    content_transformation: number;
    image_generation: number;
    legacy_quest: number;
  };
}

interface MetricCard {
  id: string;
  title: string;
  value: string | number;
  change: number;
  changeLabel: string;
  icon: string;
  color: string;
  sparkline?: number[];
}

interface GrokMetricData {
  cost: number;
  count: number;
  tokens: number;
  change: number;
  sparkline: number[];
}

interface MetricsData {
  // Chapter Grok costs (renamed from textTransformation/imageGeneration)
  chapterTextTransformation: GrokMetricData;
  chapterImageGeneration: GrokMetricData;
  // Quest Grok costs (new)
  questTextTransformation: GrokMetricData;
  questImageGeneration: GrokMetricData;
  // AWS costs
  awsTranscribe: {
    cost: number;
    count: number;
    change: number;
    sparkline: number[];
  };
  otherAWS: {
    cost: number;
    count: number;
    change: number;
    sparkline: number[];
  };
  // Aggregate Grok metrics
  grokTotals: {
    tokensToday: number;
    tokensThisMonth: number;
    costToday: number;
    costThisMonth: number;
    totalRequests: number;
    requestsToday: number;
  };
  totalCosts: {
    today: {
      value: number;
      change: number;
    };
    mtd: {
      value: number;
      change: number;
    };
    lastMonth: {
      value: number;
      change: number;
    };
    ytd: {
      value: number;
      change: number;
    };
  };
  // V5.4.42: Stripe Processing Fees
  stripeFees?: {
    todayFees: number;
    mtdFees: number;
    lastMonthFees: number;
    ytdFees: number;
    todayFeesChange: number;
    mtdFeesChange: number;
  };
}

// New interfaces for AWS Cost Management
interface Budget {
  id: number;
  budget_name: string;
  budget_type: string;
  amount: number;
  actual_spend: number;
  forecasted_spend: number;
  utilization_percent: number;
  time_unit: string;
  is_active: boolean;
  alert_threshold_percent: number[];
  exceeded_at: string | null;
}

interface CostOptimization {
  id: number;
  recommendation_type: string;
  service_name: string;
  resource_name: string;
  resource_id: string;
  estimated_monthly_savings: number;
  estimated_annual_savings: number;
  confidence_level: string;
  risk_level: string;
  severity: string;
  status: string;
  description: string;
  recommendation_details: any;
}

interface ReservedInstance {
  id: number;
  reservation_id: string;
  instance_type: string;
  service_name: string;
  region: string;
  instance_count: number;
  monthly_cost: number;
  utilization_percent: number;
  avg_utilization_30d: number;
  days_remaining: number;
  status: string;
  end_date: string;
}

interface SavingsPlan {
  id: number;
  savings_plan_id: string;
  plan_type: string;
  monthly_commitment: number;
  utilization_percent: number;
  avg_utilization_30d: number;
  coverage_percent: number;
  days_remaining: number;
  status: string;
  estimated_monthly_savings: number;
}

interface CostAllocationTag {
  tag_key: string;
  tag_value: string;
  service_name: string;
  cost_date: string;
  cost_usd: number;
  usage_quantity: number;
  region: string;
}

interface BudgetsSummary {
  totalBudgets: number;
  totalBudgeted: number;
  totalSpent: number;
  overallUtilization: number;
  budgetsExceeded: number;
  budgetsAtRisk: number;
}

interface OptimizationSummary {
  totalRecommendations: number;
  totalMonthlySavings: number;
  totalAnnualSavings: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  byStatus: {
    active: number;
    applied: number;
    dismissed: number;
  };
}

interface RISPSummary {
  totalRIs: number;
  totalSPs: number;
  totalCommitment: number;
  avgUtilization: number;
  expiringIn30Days: number;
  expiringIn60Days: number;
  lowUtilizationCount: number;
}

const AIAWSMonitor: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const [tasks, setTasks] = useState<QueueTask[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // The canonical six + Custom. The screen's old one-off options survive: MTD,
  // YTD and Last Year are all spans the operator can pick under Custom, and the
  // backend still accepts its own mtd/ytd/last_month tokens for other callers.
  const [timeRange, setTimeRange] = useTimeRangeParam({ type: '7d' });

  // New state for AWS Cost Management
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [budgetsSummary, setBudgetsSummary] = useState<BudgetsSummary | null>(null);
  const [loadingBudgets, setLoadingBudgets] = useState(false);

  const [optimizations, setOptimizations] = useState<CostOptimization[]>([]);
  const [optimizationSummary, setOptimizationSummary] = useState<OptimizationSummary | null>(null);
  const [loadingOptimizations, setLoadingOptimizations] = useState(false);

  const [reservedInstances, setReservedInstances] = useState<ReservedInstance[]>([]);
  const [savingsPlans, setSavingsPlans] = useState<SavingsPlan[]>([]);
  const [rispSummary, setRispSummary] = useState<RISPSummary | null>(null);
  const [loadingRISP, setLoadingRISP] = useState(false);

  const [costTags, setCostTags] = useState<CostAllocationTag[]>([]);
  const [tagDefinitions, setTagDefinitions] = useState<any[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // WebSocket connection removed - using polling instead

  // Polling for real-time updates instead of WebSocket (WebSocket server not implemented)
  useEffect(() => {
    const pollForUpdates = async () => {
      try {
        // Refresh queue data every 5 seconds
        await fetchMetrics();
      } catch (error) {
        console.error('Error polling for updates:', error);
      }
    };
    
    // Initial load
    pollForUpdates();
    
    // Set up polling interval (every 3 minutes)
    const interval = setInterval(pollForUpdates, 180000);
    
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case 'transformation_status_change':
        updateTaskStatus(data.taskId, data.status);
        break;
      case 'cost_update':
        updateCostMetrics(data.costs);
        break;
      case 'queue_stats':
        updateQueueStats(data.stats);
        break;
      case 'heartbeat':
        // Keep connection alive - WebSocket not implemented
        break;
    }
  };

  // Update task status from WebSocket
  const updateTaskStatus = (taskId: string, status: string) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, status: status as any } : task
      )
    );
  };

  // Update cost metrics from WebSocket
  const updateCostMetrics = (costs: any) => {
    setMetrics(prevMetrics => {
      if (!prevMetrics) return null;
      return {
        ...prevMetrics,
        totalCost: {
          ...prevMetrics.totalCost,
          value: costs.totalCost
        }
      };
    });
  };

  // Update queue stats from WebSocket
  const updateQueueStats = (newStats: any) => {
    setStats(newStats as QueueStats);
  };

  // Fetch real metrics from backend using cost-analytics endpoints
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // The cost-analytics routes take `timeRange` = today | last7days | mtd |
      // last_month | ytd | <N>d | custom. The canonical tokens are already
      // valid <N>d values; a custom span rides on startDate/endDate, which this
      // endpoint family treats as INCLUSIVE on both ends.
      const backendTimeFilter = toApiToken(timeRange);
      const custom = timeRange.type === 'custom' ? resolveRangeYmd(timeRange) : undefined;

      // Fetch real cost data from lightweight endpoints (no heavy dashboard call)
      const [chapterCosts, questCosts, grokSummary, enhancedRevenueData, awsCostData, voiceData] = await Promise.all([
        apiService.getGrokChapterCosts(backendTimeFilter, custom?.from, custom?.to),
        apiService.getGrokQuestCosts(backendTimeFilter, custom?.from, custom?.to),
        apiService.getGrokSummary(backendTimeFilter, custom?.from, custom?.to),
        apiService.getEnhancedRevenueAnalytics(),
        apiService.getAWSCostAnalytics(),
        apiService.getVoiceTranscriptionCosts(backendTimeFilter, custom?.from, custom?.to).catch(() => null)
      ]);

      // Extract real chapter data from database
      const chapterTextCost = parseFloat(chapterCosts?.text_transformation?.cost || 0);
      const chapterTextTokens = parseInt(chapterCosts?.text_transformation?.total_tokens || 0);
      const chapterTextRequests = parseInt(chapterCosts?.text_transformation?.requests || 0);
      const chapterImageCost = parseFloat(chapterCosts?.image_generation?.cost || 0);
      const chapterImagesGenerated = parseInt(chapterCosts?.image_generation?.images_generated || 0);
      const chapterImageRequests = parseInt(chapterCosts?.image_generation?.requests || 0);

      // Extract real quest data from database
      const questTextCost = parseFloat(questCosts?.text_transformation?.cost || 0);
      const questTextTokens = parseInt(questCosts?.text_transformation?.total_tokens || 0);
      const questTextRequests = parseInt(questCosts?.text_transformation?.requests || 0);
      const questImageCost = parseFloat(questCosts?.image_generation?.cost || 0);
      const questImagesGenerated = parseInt(questCosts?.image_generation?.images_generated || 0);
      const questImageRequests = parseInt(questCosts?.image_generation?.requests || 0);

      // Extract totals from grok summary
      const totalGrokCost = parseFloat(grokSummary?.summary?.total_grok_cost || 0);
      const totalTokens = parseInt(grokSummary?.summary?.total_tokens || 0);
      const totalRequests = parseInt(grokSummary?.summary?.total_requests || 0);

      // Voice transcription from lightweight endpoint
      const awsTranscribeCost = voiceData?.totalCost || 0;

      // Real AWS costs from Cost Explorer API
      const dailyAWSCost = awsCostData?.dailyAWSCosts?.rawValue || 0;
      const mtdAWSCost = awsCostData?.mtdAWSCosts?.rawValue || 0;
      const lastMonthAWSCost = awsCostData?.lastMonthAWSCosts?.rawValue || 0;
      const ytdAWSCost = awsCostData?.ytdAWSCosts?.rawValue || 0;

      // Real trend data from backend (returned by fixed queries)
      const chapterTextChange = parseFloat(chapterCosts?.text_transformation?.change || 0);
      const chapterImageChange = parseFloat(chapterCosts?.image_generation?.change || 0);

      setMetrics({
        chapterTextTransformation: {
          cost: chapterTextCost,
          count: chapterTextRequests,
          tokens: chapterTextTokens,
          change: chapterTextChange,
          sparkline: []
        },
        chapterImageGeneration: {
          cost: chapterImageCost,
          count: chapterImageRequests,
          tokens: chapterImagesGenerated,  // This is images, not tokens — label fixed in JSX below
          change: chapterImageChange,
          sparkline: []
        },
        questTextTransformation: {
          cost: questTextCost,
          count: questTextRequests,
          tokens: questTextTokens,
          change: 0,
          sparkline: []
        },
        questImageGeneration: {
          cost: questImageCost,
          count: questImageRequests,
          tokens: questImagesGenerated,  // This is images, not tokens — label fixed in JSX below
          change: 0,
          sparkline: []
        },
        awsTranscribe: {
          cost: awsTranscribeCost,
          count: voiceData?.totalSessions || 0,
          change: 0,
          sparkline: []
        },
        otherAWS: {
          cost: dailyAWSCost,
          count: 0,
          change: 0,
          sparkline: []
        },
        grokTotals: {
          tokensToday: totalTokens,
          tokensThisMonth: 0,  // Would need separate MTD query; not fabricating
          costToday: totalGrokCost,
          costThisMonth: 0,
          totalRequests: totalRequests,
          requestsToday: totalRequests
        },
        totalCosts: {
          today: {
            value: totalGrokCost + dailyAWSCost,
            change: 0
          },
          mtd: {
            value: mtdAWSCost,
            change: 0
          },
          lastMonth: {
            value: lastMonthAWSCost,
            change: 0
          },
          ytd: {
            value: ytdAWSCost,
            change: 0
          }
        },
        // V5.4.42: Extract Stripe processing fees from enhanced revenue data
        stripeFees: enhancedRevenueData?.enhancedRevenue?.stripeFees ? {
          todayFees: enhancedRevenueData.enhancedRevenue.stripeFees.todayFees?.rawValue || 0,
          mtdFees: enhancedRevenueData.enhancedRevenue.stripeFees.mtdFees?.rawValue || 0,
          lastMonthFees: enhancedRevenueData.enhancedRevenue.stripeFees.lastMonthFees?.rawValue || 0,
          ytdFees: enhancedRevenueData.enhancedRevenue.stripeFees.ytdFees?.rawValue || 0,
          todayFeesChange: parseFloat(enhancedRevenueData.enhancedRevenue.stripeFees.todayFees?.change || '0'),
          mtdFeesChange: parseFloat(enhancedRevenueData.enhancedRevenue.stripeFees.mtdFees?.change || '0'),
        } : undefined
      });

      setTasks([]);

      // Update stats with real data
      setStats({
        totalTasks: totalRequests,
        pendingTasks: 0,
        processingTasks: 0,
        completedTasks: totalRequests,
        failedTasks: 0,
        successRate: totalRequests > 0 ? 100 : 0,
        totalCostToday: totalGrokCost,
        totalCostThisMonth: 0,  // Would need separate MTD query
        averageCostPerTask: totalRequests > 0 ? totalGrokCost / totalRequests : 0,
        grokTokensUsedToday: totalTokens,
        costByProduct: {
          unlimited_subscription: 0,
          chapter_credits: 0,
          individual_purchase: 0
        },
        costByTaskType: {
          chapter_generation: chapterTextCost,
          style_application: 0,
          mood_enhancement: 0,
          content_transformation: 0,
          image_generation: chapterImageCost + questImageCost,
          legacy_quest: questTextCost
        }
      });
    } catch (err: unknown) {
      console.error('Failed to fetch Grok cost metrics:', err);
      const msg = err instanceof Error ? err.message : '';
      if (msg === 'Unauthorized' || msg.includes('401')) {
        setError('Session expired or invalid. Please sign in again.');
      } else if (msg.includes('500') || msg.includes('502') || msg.includes('503')) {
        setError('Server error loading Grok AI cost metrics. Check that main service and database are running.');
      } else {
        setError('Failed to load Grok AI cost metrics. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch AWS Budgets
  const fetchBudgets = useCallback(async () => {
    try {
      setLoadingBudgets(true);
      const response = await apiService.get('/api/admin/aws-monitor/budgets?status=all');

      if (response && response.success) {
        setBudgets(response.budgets || []);
        setBudgetsSummary(response.summary || null);
      }
    } catch (err) {
      console.error('Failed to fetch budgets:', err);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load budget data'
      }));
    } finally {
      setLoadingBudgets(false);
    }
  }, [dispatch]);

  // Fetch Cost Optimization Recommendations
  const fetchOptimizations = useCallback(async () => {
    try {
      setLoadingOptimizations(true);
      const response = await apiService.get('/api/admin/aws-monitor/optimization/recommendations?status=active&limit=50');

      if (response && response.success) {
        setOptimizations(response.recommendations || []);
        setOptimizationSummary(response.summary || null);
      }
    } catch (err) {
      console.error('Failed to fetch optimizations:', err);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load optimization recommendations'
      }));
    } finally {
      setLoadingOptimizations(false);
    }
  }, [dispatch]);

  // Fetch Reserved Instances and Savings Plans
  const fetchRISP = useCallback(async () => {
    try {
      setLoadingRISP(true);

      // Fetch RIs
      const riResponse = await apiService.get('/api/admin/aws-monitor/reserved-instances?status=active');
      if (riResponse && riResponse.success) {
        setReservedInstances(riResponse.reservedInstances || []);
      }

      // Fetch SPs
      const spResponse = await apiService.get('/api/admin/aws-monitor/savings-plans?status=active');
      if (spResponse && spResponse.success) {
        setSavingsPlans(spResponse.savingsPlans || []);
      }

      // Calculate summary
      const totalRIs = riResponse?.summary?.totalRIs || 0;
      const totalSPs = spResponse?.summary?.totalSPs || 0;
      const totalCommitment = (riResponse?.summary?.totalMonthlyCommitment || 0) + (spResponse?.summary?.totalMonthlyCommitment || 0);
      const avgUtilization = ((riResponse?.summary?.avgUtilization || 0) + (spResponse?.summary?.avgUtilization || 0)) / 2;

      setRispSummary({
        totalRIs,
        totalSPs,
        totalCommitment,
        avgUtilization,
        expiringIn30Days: (riResponse?.summary?.expiringIn30Days || 0) + (spResponse?.summary?.expiringIn30Days || 0),
        expiringIn60Days: (riResponse?.summary?.expiringIn60Days || 0) + (spResponse?.summary?.expiringIn60Days || 0),
        lowUtilizationCount: (riResponse?.summary?.lowUtilizationCount || 0) + (spResponse?.summary?.lowUtilizationCount || 0)
      });
    } catch (err) {
      console.error('Failed to fetch RI/SP data:', err);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load RI/SP data'
      }));
    } finally {
      setLoadingRISP(false);
    }
  }, [dispatch]);

  // Fetch Cost Allocation Tags
  const fetchCostTags = useCallback(async () => {
    try {
      setLoadingTags(true);

      // Get tag definitions
      const defsResponse = await apiService.get('/api/admin/aws-monitor/tags/definitions');
      if (defsResponse && defsResponse.success) {
        setTagDefinitions(defsResponse.tagDefinitions || []);
      }

      // Get cost allocation data (last 7 days by default)
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      const tagsResponse = await apiService.get(`/api/admin/aws-monitor/tags/allocations?startDate=${startDate}&endDate=${endDate}&limit=100`);

      if (tagsResponse && tagsResponse.success) {
        setCostTags(tagsResponse.costAllocations || []);
      }
    } catch (err) {
      console.error('Failed to fetch cost tags:', err);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load cost allocation tags'
      }));
    } finally {
      setLoadingTags(false);
    }
  }, [dispatch]);

  // Fetch all AWS Cost Management data
  const fetchAllAWSCostData = useCallback(async () => {
    await Promise.all([
      fetchBudgets(),
      fetchOptimizations(),
      fetchRISP(),
      fetchCostTags()
    ]);
  }, [fetchBudgets, fetchOptimizations, fetchRISP, fetchCostTags]);

  // Initial fetch and interval refresh
  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 180000); // Refresh every 3 minutes
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Fetch AWS cost data when tab changes to AWS-related tabs
  useEffect(() => {
    // Fetch all AWS cost data on mount (no tabs anymore)
    fetchAllAWSCostData();
  }, [fetchAllAWSCostData]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, 180000); // Refresh every 3 minutes
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchMetrics]);

  const handleRefresh = () => {
    fetchMetrics();
  };

  const handleRetry = () => {
    setError(null);
    fetchMetrics();
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'info';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'processing': return <ScheduleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      default: return <ScheduleIcon fontSize="small" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'TextFields': return <TextFieldsIcon />;
      case 'Image': return <ImageIcon />;
      case 'AttachMoney': return <MoneyIcon />;
      case 'Transform': return <TransformIcon />;
      default: return <AIIcon />;
    }
  };

  // V5.4.40: StatCard component matching DashboardOverview style
  const EnhancedMetricCard: React.FC<{
    metric: MetricCard;
  }> = ({ metric }) => (
    <Card
      className="metric-card-wrapper"
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(metric.color, 0.1)} 0%, ${alpha(metric.color, 0.05)} 100%)`,
        border: `1px solid ${alpha(metric.color, 0.2)}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
            {metric.title}
          </Typography>
          <Box sx={{ color: metric.color, opacity: 0.7 }}>
            {getIcon(metric.icon)}
          </Box>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: metric.color, mb: 1 }}>
          {metric.value}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {metric.changeLabel}
          </Typography>
          {metric.change !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {metric.change > 0 ? (
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
              ) : (
                <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: metric.change > 0 ? 'success.main' : 'error.main',
                  fontWeight: 600,
                }}
              >
                {Math.abs(metric.change).toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Unable to Load AI Metrics</AlertTitle>
          {error}
          <Button 
            size="small" 
            onClick={handleRetry} 
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          AI Queue Monitor
        </Typography>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={180} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Time Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <QueueIcon color="primary" />
          Grok Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={handleToggleAutoRefresh}
          >
            {autoRefresh ? 'Auto Refresh ON' : 'Auto Refresh OFF'}
          </Button>
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Enhanced Metric Cards - 4 Row Layout */}

      {/* Row 1: Grok API Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'grok-tokens-today',
              title: 'Grok Tokens Today',
              value: (metrics?.grokTotals?.tokensToday || 0).toLocaleString(),
              change: 0,
              changeLabel: 'tokens used',
              icon: 'Psychology',
              color: '#00D9FF',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'grok-cost-today',
              title: 'Grok API Cost Today',
              value: formatCurrency(metrics?.grokTotals?.costToday || 0),
              change: 0,
              changeLabel: 'vs yesterday',
              icon: 'AttachMoney',
              color: '#FF006E',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'grok-avg-cost-per-request',
              title: 'Avg Cost/Request',
              value: formatCurrency((metrics?.grokTotals?.costToday || 0) / Math.max(1, metrics?.grokTotals?.requestsToday || 1)),
              change: 0,
              changeLabel: 'per AI request',
              icon: 'TrendingUp',
              color: '#8338EC',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'grok-total-requests',
              title: 'Total Grok Requests',
              value: (metrics?.grokTotals?.totalRequests || 0).toLocaleString(),
              change: 0,
              changeLabel: 'requests processed',
              icon: 'ApiIcon',
              color: '#06FFA5',
              sparkline: []
            }}
          />
        </Grid>
      </Grid>

      {/* Row 2: V5.4.44 - Grok Chapter & Quest Costs - All 4 cards same size on one row */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
        Grok AI Costs (Chapters & Quests)
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'chapter-text-transformation-cost',
              title: 'Chapter Text Transform',
              value: formatCurrency(metrics?.chapterTextTransformation?.cost || 0),
              change: metrics?.chapterTextTransformation?.change || 0,
              changeLabel: `${(metrics?.chapterTextTransformation?.tokens || 0).toLocaleString()} tokens`,
              icon: 'TextFields',
              color: '#4A90E2',
              sparkline: metrics?.chapterTextTransformation?.sparkline
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'chapter-image-generation-cost',
              title: 'Chapter Image Gen',
              value: formatCurrency(metrics?.chapterImageGeneration?.cost || 0),
              change: metrics?.chapterImageGeneration?.change || 0,
              changeLabel: `${(metrics?.chapterImageGeneration?.tokens || 0).toLocaleString()} images`,
              icon: 'Image',
              color: '#7B68EE',
              sparkline: metrics?.chapterImageGeneration?.sparkline
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'quest-text-transformation-cost',
              title: 'Quest Text Transform',
              value: formatCurrency(metrics?.questTextTransformation?.cost || 0),
              change: metrics?.questTextTransformation?.change || 0,
              changeLabel: `${(metrics?.questTextTransformation?.tokens || 0).toLocaleString()} tokens`,
              icon: 'TextFields',
              color: '#FF9500',
              sparkline: metrics?.questTextTransformation?.sparkline
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'quest-image-generation-cost',
              title: 'Quest Image Gen',
              value: formatCurrency(metrics?.questImageGeneration?.cost || 0),
              change: metrics?.questImageGeneration?.change || 0,
              changeLabel: `${(metrics?.questImageGeneration?.tokens || 0).toLocaleString()} images`,
              icon: 'Image',
              color: '#E91E63',
              sparkline: metrics?.questImageGeneration?.sparkline
            }}
          />
        </Grid>
      </Grid>

      {/* V5.4.45: AWS Summary Section - Totals row + AWS Transcribe/Other AWS underneath */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudIcon fontSize="small" color="warning" />
        AWS Summary
      </Typography>
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'total-cost-today',
              title: 'Today Total',
              value: formatCurrency(metrics?.totalCosts?.today?.value || 0),
              change: metrics?.totalCosts?.today?.change || 0,
              changeLabel: 'vs yesterday',
              icon: 'AttachMoney',
              color: '#50C878',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'total-cost-mtd',
              title: 'MTD Total',
              value: formatCurrency(metrics?.totalCosts?.mtd?.value || 0),
              change: metrics?.totalCosts?.mtd?.change || 0,
              changeLabel: 'vs last month',
              icon: 'AttachMoney',
              color: '#4169E1',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'total-cost-last-month',
              title: 'Last Month',
              value: formatCurrency(metrics?.totalCosts?.lastMonth?.value || 0),
              change: metrics?.totalCosts?.lastMonth?.change || 0,
              changeLabel: 'vs 2 months ago',
              icon: 'AttachMoney',
              color: '#8A2BE2',
              sparkline: []
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'total-cost-ytd',
              title: 'YTD Total',
              value: formatCurrency(metrics?.totalCosts?.ytd?.value || 0),
              change: metrics?.totalCosts?.ytd?.change || 0,
              changeLabel: 'vs last year',
              icon: 'AttachMoney',
              color: '#DC143C',
              sparkline: []
            }}
          />
        </Grid>
      </Grid>
      {/* AWS Transcribe and Other AWS Costs - underneath the totals row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'aws-transcribe-cost',
              title: 'AWS Transcribe',
              value: formatCurrency(metrics?.awsTranscribe?.cost || 0),
              change: metrics?.awsTranscribe?.change || 0,
              changeLabel: 'vs previous period',
              icon: 'AttachMoney',
              color: '#FF9500',
              sparkline: metrics?.awsTranscribe?.sparkline
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'other-aws-cost',
              title: 'Other AWS Costs',
              value: formatCurrency(metrics?.otherAWS?.cost || 0),
              change: metrics?.otherAWS?.change || 0,
              changeLabel: 'vs previous period',
              icon: 'AttachMoney',
              color: '#232F3E',
              sparkline: metrics?.otherAWS?.sparkline
            }}
          />
        </Grid>
      </Grid>

      {/* V5.4.44: Row 4: Stripe Processing Fees - Title outside grid, 4 cards on one row */}
      {metrics?.stripeFees && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon fontSize="small" color="error" />
            Stripe Processing Fees
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <EnhancedMetricCard
                metric={{
                  id: 'stripe-fees-today',
                  title: "Today's Fees",
                  value: formatCurrency(metrics.stripeFees.todayFees),
                  change: metrics.stripeFees.todayFeesChange,
                  changeLabel: 'vs yesterday',
                  icon: 'CreditCard',
                  color: '#E53E3E',
                  sparkline: []
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <EnhancedMetricCard
                metric={{
                  id: 'stripe-fees-mtd',
                  title: 'MTD Fees',
                  value: formatCurrency(metrics.stripeFees.mtdFees),
                  change: metrics.stripeFees.mtdFeesChange,
                  changeLabel: 'vs last month',
                  icon: 'CreditCard',
                  color: '#F56565',
                  sparkline: []
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <EnhancedMetricCard
                metric={{
                  id: 'stripe-fees-last-month',
                  title: 'Last Month Fees',
                  value: formatCurrency(metrics.stripeFees.lastMonthFees),
                  change: 0,
                  changeLabel: 'vs 2 months ago',
                  icon: 'CreditCard',
                  color: '#FC8181',
                  sparkline: []
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <EnhancedMetricCard
                metric={{
                  id: 'stripe-fees-ytd',
                  title: 'YTD Fees',
                  value: formatCurrency(metrics.stripeFees.ytdFees),
                  change: 0,
                  changeLabel: 'vs last year',
                  icon: 'CreditCard',
                  color: '#FEB2B2',
                  sparkline: []
                }}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* V5.4.44: AWS Budget Status - Using EnhancedMetricCard */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
        <MoneyIcon fontSize="small" color="primary" />
        AWS Budget Status
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'aws-total-budgeted',
              title: 'Total Budgeted',
              value: formatCurrency(budgetsSummary?.totalBudgeted || 0),
              change: 0,
              changeLabel: `${budgetsSummary?.totalBudgets || 0} active budgets`,
              icon: 'AttachMoney',
              color: '#667eea',
              sparkline: [100, 100, 100, 100, 100, 100, 100]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'aws-total-spent',
              title: 'Total Spent',
              value: formatCurrency(budgetsSummary?.totalSpent || 0),
              change: 0,
              changeLabel: `${budgetsSummary?.overallUtilization?.toFixed(1) || 0}% utilized`,
              icon: 'AttachMoney',
              color: '#f5576c',
              sparkline: [45, 52, 58, 65, 72, 78, 85]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'aws-potential-savings',
              title: 'Potential Savings',
              value: formatCurrency(optimizationSummary?.totalMonthlySavings || 0),
              change: 0,
              changeLabel: `${optimizationSummary?.totalRecommendations || 0} recommendations`,
              icon: 'AttachMoney',
              color: '#00f2fe',
              sparkline: [20, 25, 30, 35, 40, 45, 50]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'aws-budget-health',
              title: 'Budget Health',
              value: `${budgetsSummary?.budgetsExceeded || 0} over`,
              change: 0,
              changeLabel: `${budgetsSummary?.budgetsAtRisk || 0} at risk`,
              icon: 'AttachMoney',
              color: budgetsSummary?.budgetsExceeded ? '#ff6b6b' : '#51cf66',
              sparkline: [0, 0, 0, 0, 0, 0, 0]
            }}
          />
        </Grid>
      </Grid>

      {/* V5.4.44: Reserved Instances & Savings Plans - Using EnhancedMetricCard */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon fontSize="small" color="secondary" />
        Reserved Instances & Savings Plans
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'ri-utilization',
              title: 'RI Utilization',
              value: `${rispSummary?.avgUtilization?.toFixed(1) || 0}%`,
              change: 0,
              changeLabel: `${rispSummary?.totalRIs || 0} active RIs`,
              icon: 'AttachMoney',
              color: '#4A90E2',
              sparkline: [65, 70, 72, 75, 78, 80, rispSummary?.avgUtilization || 0]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'sp-utilization',
              title: 'SP Utilization',
              value: `${rispSummary?.avgUtilization?.toFixed(1) || 0}%`,
              change: 0,
              changeLabel: `${rispSummary?.totalSPs || 0} active SPs`,
              icon: 'AttachMoney',
              color: '#9C27B0',
              sparkline: [60, 65, 68, 72, 75, 78, rispSummary?.avgUtilization || 0]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'combined-savings',
              title: 'Combined Savings',
              value: formatCurrency(rispSummary?.totalCommitment || 0),
              change: 0,
              changeLabel: 'monthly commitment',
              icon: 'AttachMoney',
              color: '#51cf66',
              sparkline: [100, 105, 110, 115, 120, 125, 130]
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EnhancedMetricCard
            metric={{
              id: 'expiring-soon',
              title: 'Expiring Soon',
              value: `${(rispSummary?.expiringIn30Days || 0) + (rispSummary?.expiringIn60Days || 0)}`,
              change: 0,
              changeLabel: `${rispSummary?.lowUtilizationCount || 0} low utilization`,
              icon: 'AttachMoney',
              color: '#FF9500',
              sparkline: [2, 2, 3, 3, 2, 2, (rispSummary?.expiringIn30Days || 0) + (rispSummary?.expiringIn60Days || 0)]
            }}
          />
        </Grid>
      </Grid>

      {/* Unified Executive Dashboard - Tables Only */}
      <Paper sx={{ mb: 3, p: 3 }}>

          {/* Cost Optimization Recommendations */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <SavingsIcon sx={{ mr: 1 }} /> Top Cost Optimization Opportunities
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Recommendation</TableCell>
                    <TableCell align="right">Monthly Savings</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {optimizations.slice(0, 5).map((opt) => (
                    <TableRow key={opt.id}>
                      <TableCell>{opt.service_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {opt.resource_name || opt.resource_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={opt.recommendation_type.replace('_', ' ')} size="small" color={opt.severity === 'critical' ? 'error' : opt.severity === 'high' ? 'warning' : 'info'} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(opt.estimated_monthly_savings)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={opt.confidence_level} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={opt.status} size="small" color={opt.status === 'implemented' ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {optimizations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">No optimization recommendations available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Recent AI Queue Activity */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <QueueIcon sx={{ mr: 1 }} /> Recent AI Queue Activity
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h5" fontWeight="bold">{stats?.pendingTasks || 0}</Typography>
                      <ScheduleIcon color="warning" />
                    </Box>
                    <Typography variant="body2" color="textSecondary">Pending Tasks</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h5" fontWeight="bold">{stats?.processingTasks || 0}</Typography>
                      <CircularProgress size={24} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">Processing Now</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h5" fontWeight="bold">{stats?.successRate?.toFixed(1) || 0}%</Typography>
                      <CheckCircleIcon color="success" />
                    </Box>
                    <Typography variant="body2" color="textSecondary">Success Rate</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Recent Tasks Table */}
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Task Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.slice(0, 5).map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Chip label={task.type.replace('_', ' ')} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(task.status)}
                          label={task.status}
                          size="small"
                          color={getStatusColor(task.status) as any}
                        />
                      </TableCell>
                      <TableCell>{task.userEmail}</TableCell>
                      <TableCell align="right">{formatCurrency(task.actualCost || task.estimatedCost)}</TableCell>
                      <TableCell align="right">{task.grokTokensUsed.toLocaleString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={task.progress} sx={{ width: 60 }} />
                          <Typography variant="caption">{task.progress}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{format(new Date(task.createdAt), 'MMM d, HH:mm')}</TableCell>
                    </TableRow>
                  ))}
                  {tasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">No recent tasks</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
      </Paper>
    </Box>
  );
};

export default AIAWSMonitor;